import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { Button } from './ui/button';
import { Copy, ExternalLink, QrCode, PenLine, Zap, Download, X, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import TabPageLayout from './TabPageLayout';
import WaiversTabClean from './WaiversTabClean';
import KioskFlowBuilder from './KioskFlowBuilder';
import apiV2 from '../utils/apiV2';
import ServiceToggle from './ServiceToggle';

const QrModal = ({ title, url, onClose }) => {
  const downloadQr = () => {
    const svg = document.querySelector('#walkins-qr-svg svg');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = 'walkins-qr.svg';
    a.click();
    URL.revokeObjectURL(href);
    toast.success('QR downloaded');
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="walkins-qr-modal">
      <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="walkins-qr-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div id="walkins-qr-svg" className="bg-white p-4 border border-gray-200 rounded-xl inline-block">
          <QRCode value={url} size={200} level="M" />
        </div>
        <p className="text-xs text-gray-500 mt-3 break-all font-mono">{url}</p>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" className="flex-1 gap-1.5" onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copied'); }}>
            <Copy className="w-4 h-4" /> Copy link
          </Button>
          <Button className="flex-1 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white" onClick={downloadQr} data-testid="walkins-qr-download">
            <Download className="w-4 h-4" /> Download
          </Button>
        </div>
      </div>
    </div>
  );
};

// "Which kiosk?" chooser removed — Open kiosk goes straight to Kiosk Direct.
// The classic calendar kiosk stays reachable at /kiosk/{slug} (no ?mode=direct).

// ── Point of Sale configuration ──────────────────────────────────────────────
// What the walk-in POS (Kiosk Direct) shows and requires. Stored in
// org.settings.posConfig via /settings/pos-config. Name defaults to required
// but can be optional/hidden for quick low-value tickets (no name needed).
const POS_FIELDS = [
  { key: 'name', label: 'Guest name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone number' },
  { key: 'nationality', label: 'Nationality' },
  { key: 'idNumber', label: 'ID / passport number' },
];

const PosConfigCard = () => {
  const orgId = localStorage.getItem('organizationId');
  const [cfg, setCfg] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    Promise.all([
      apiV2.get(`/organizations/${orgId}/settings/pos-config`),
      apiV2.get(`/organizations/${orgId}/services`),
    ]).then(([c, s]) => {
      if (dead) return;
      setCfg(c.data);
      const list = s.data?.services || s.data || [];
      setServices(list.filter((x) => x.isActive !== false && x.is_active !== false));
    }).catch(() => { /* card simply stays hidden on failure */ })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [orgId]);

  const save = async (next) => {
    setCfg(next);
    try {
      const r = await apiV2.put(`/organizations/${orgId}/settings/pos-config`, next);
      setCfg(r.data);
    } catch {
      toast.error('Could not save POS settings');
    }
  };

  if (loading || !cfg) return null;

  const allIds = services.map((s) => s.id);
  const enabled = cfg.services === null ? allIds : cfg.services;
  const toggleService = (id, on) => {
    let next = on ? [...new Set([...enabled, id])] : enabled.filter((x) => x !== id);
    // Every service on → store null so NEW services auto-appear on the POS.
    save({ ...cfg, services: next.length === allIds.length ? null : next });
  };

  return (
    <div className="card-soft p-5 sm:p-6" data-testid="walkins-pos-config">
      <h3 className="settings-section-title">Point of Sale setup</h3>
      <p className="settings-section-subtitle mb-5">
        What the kiosk shows and asks for during a walk-in checkout. Name is always collected — it links the waiver to the booking.
      </p>

      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Services on the POS</p>
      <div className="divide-y divide-gray-50 mb-5">
        {services.map((s) => (
          <div key={s.id} className="py-2.5 flex items-center gap-3" data-testid={`pos-service-row-${s.id}`}>
            <span className="text-sm text-gray-700 flex-1 truncate">{s.name}</span>
            <span data-testid={`pos-service-toggle-${s.id}`}>
              <ServiceToggle checked={enabled.includes(s.id)} onChange={(v) => toggleService(s.id, v)} />
            </span>
          </div>
        ))}
        {services.length === 0 && <p className="text-xs text-gray-400 py-2">No active services yet.</p>}
      </div>

      <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">Customer fields at checkout</p>
      <div className="space-y-2 mb-5">
        {POS_FIELDS.map(({ key, label }) => (
          <div key={key} className="flex items-center gap-3">
            <span className="text-sm text-gray-700 flex-1">{label}</span>
            <select
              value={cfg.fields?.[key] || 'optional'}
              onChange={(e) => save({ ...cfg, fields: { ...cfg.fields, [key]: e.target.value } })}
              className="h-9 rounded-md border border-input bg-white px-2.5 text-sm text-gray-700"
              data-testid={`pos-field-${key}`}
            >
              <option value="required">Required</option>
              <option value="optional">Optional</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 pt-1">
        <div className="flex-1">
          <p className="text-sm text-gray-700">OTP verification on kiosk waivers</p>
          <p className="text-[11px] text-gray-400">Text/email a code before signing — turn off for faster walk-in lines</p>
        </div>
        <span data-testid="pos-waiver-otp-toggle">
          <ServiceToggle checked={!!cfg.waiverOtp} onChange={(v) => save({ ...cfg, waiverOtp: v })} />
        </span>
      </div>

      <div className="flex items-center gap-3 pt-4 mt-4 border-t border-gray-50">
        <div className="flex-1">
          <p className="text-sm text-gray-700">Require signed waiver before checkout</p>
          <p className="text-[11px] text-gray-400">At least one signature on the POS sheet before payment — off lets guests pay first and sign after</p>
        </div>
        <span data-testid="pos-require-waiver-toggle">
          <ServiceToggle checked={!!cfg.requireWaiver} onChange={(v) => save({ ...cfg, requireWaiver: v })} />
        </span>
      </div>
    </div>
  );
};

const WalkInsTab = ({ onNavigate }) => {
  const orgSlug = localStorage.getItem('organizationSlug') || localStorage.getItem('orgSlug') || '';
  const origin = window.location.origin;
  const bookingUrl = `${origin}/services-grid.html?org=${orgSlug}&flow=walk-ins`;
  const kioskUrl = `${origin}/kiosk/${orgSlug}`;
  const signUrl = `${origin}/sign/${orgSlug}`;
  const [qr, setQr] = useState(null);
  const [flowBuilder, setFlowBuilder] = useState(false);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Link copied'); };

  // Kiosk mode: always Kiosk Direct. If the operator has Check-In enabled, hand
  // the kiosk a 12h scanner session so staff get the camera check-in view.
  // Same-window navigation — wrapped/native apps often block popups.
  const openKiosk = async () => {
    let url = `${kioskUrl}?mode=direct`;
    try {
      const orgRes = await apiV2.get('/auth/organizations/me');
      if (orgRes.data?.checkInEnabled) {
        const s = await apiV2.post('/check-in/create-scanner-session');
        if (s.data?.token) url += `&scanner=${s.data.token}`;
      }
    } catch (e) { /* open plain kiosk */ }
    window.location.assign(url);
  };

  return (
    <TabPageLayout
      title="Walk-Ins"
      subtitle="Everything for in-person customers — book, pay and sign, in one place"
    >
      <div className="space-y-6" data-testid="walkins-tab">
        {/* Kiosk launcher — one blue button, chooser picks Direct vs Classic */}
        <div className="card-soft p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white gap-2 rounded-full px-6"
              onClick={() => openKiosk()}
              data-testid="walkins-open-kiosk-btn"
            >
              <Zap className="w-4 h-4" /> Open kiosk
            </Button>
            <span className="hidden sm:block w-px h-6 bg-gray-200" />
            <Button
              variant="outline"
              className="gap-2 rounded-full"
              onClick={() => setQr({ title: 'Scan to book', url: bookingUrl })}
              data-testid="walkins-qr-btn"
            >
              <QrCode className="w-4 h-4" /> Booking QR
            </Button>
            <Button
              variant="outline"
              className="gap-2 rounded-full"
              onClick={() => setQr({ title: 'Scan to sign the waiver', url: signUrl })}
              data-testid="walkins-waiver-qr-btn"
            >
              <PenLine className="w-4 h-4" /> Waiver QR
            </Button>
            <Button
              variant="outline"
              className="gap-2 rounded-full"
              onClick={() => setFlowBuilder(true)}
              data-testid="walkins-customize-flow-btn"
            >
              <SlidersHorizontal className="w-4 h-4" /> Customize flow
            </Button>
          </div>
          <p className="text-xs text-gray-600 mt-3" data-testid="walkins-prep-note">
            <span className="font-semibold text-gray-800">Before your first walk-in:</span>{' '}
            walk-ins book right now — set <span className="font-medium">Minimum notice</span> to 0 hours on the services you sell at the door, or same-moment time slots won&apos;t appear.{' '}
            <button
              type="button"
              className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
              onClick={() => onNavigate && onNavigate('services')}
              data-testid="walkins-review-services-link"
            >
              Review services
            </button>
            <span className="text-gray-300"> · </span>
            <button
              type="button"
              className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
              onClick={() => onNavigate && onNavigate('flows')}
              data-testid="walkins-edit-flow-link"
            >
              Edit Walk-Ins flow
            </button>
          </p>
        </div>

        {/* Point of Sale configuration — what the kiosk shows & requires */}
        <PosConfigCard />

        {/* Waiver signing */}
        <div className="card-soft p-5 sm:p-6" data-testid="walkins-waiver-card">
          <h3 className="settings-section-title">Sign the waiver</h3>
          <p className="settings-section-subtitle">
            Two ways to get the signature — after checkout, customers automatically get a ticket with its own Sign Waiver button. Or they can sign right here on the spot: have them enter the confirmation code from their ticket to link the signature to the booking. Either way, you get it.
          </p>
          <div className="flex gap-2 mt-4 flex-wrap sm:flex-nowrap">
            <input
              readOnly
              value={signUrl}
              className="flex-1 min-w-[200px] text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 font-mono text-gray-700"
              data-testid="walkins-sign-url"
            />
            <Button variant="outline" size="sm" onClick={() => copy(signUrl)} className="gap-1.5" data-testid="walkins-copy-waiver-btn">
              <Copy className="w-4 h-4" /> Copy
            </Button>
            <Button
              size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
              onClick={() => window.open(signUrl, '_blank')}
              data-testid="walkins-open-waiver-btn"
            >
              <ExternalLink className="w-4 h-4" /> Open
            </Button>
          </div>
        </div>

        {/* Signed waivers — the exact same component as the Waivers tab */}
        <div data-testid="walkins-signed-waivers">
          <WaiversTabClean signedOnly />
        </div>
      </div>

      {qr && <QrModal title={qr.title} url={qr.url} onClose={() => setQr(null)} />}
      {flowBuilder && <KioskFlowBuilder onClose={() => setFlowBuilder(false)} />}
    </TabPageLayout>
  );
};

export default WalkInsTab;
